import { useI18n } from '@/i18n'
import { CaretDown, Check, MagnifyingGlass, X } from '@/shared/icons/phosphor'
import { DropdownMenu } from '@kobalte/core/dropdown-menu'
import { For, type JSX, Show, createMemo, createSignal } from 'solid-js'
import { Badge } from '../../primitives/Badge'
import { foldForSearch } from '../RichSelect/RichSelect'

export type MultiSelectValue = string | number
export type MultiSelectDisplay = 'count' | 'tags'
export type MultiSelectTriggerVariant = 'value' | 'title'

export interface MultiSelectOption<T extends MultiSelectValue = string> {
  value: T
  label: string
  description?: string
}

export interface MultiSelectProps<T extends MultiSelectValue = string> {
  options: MultiSelectOption<T>[]
  selected: T[]
  onChange: (selected: T[]) => void
  display?: MultiSelectDisplay
  label?: string
  placeholder?: string
  searchable?: boolean
  showSelectAll?: boolean
  groupSelected?: boolean
  trigger?: JSX.Element
  triggerVariant?: MultiSelectTriggerVariant
  disabled?: boolean
  class?: string
}

export function MultiSelect<T extends MultiSelectValue = string>(props: MultiSelectProps<T>) {
  const { t } = useI18n()
  const [isOpen, setIsOpen] = createSignal(false)
  const [query, setQuery] = createSignal('')

  const display = () => props.display ?? 'count'
  const searchable = () => props.searchable ?? display() === 'tags'
  const showSelectAll = () => props.showSelectAll ?? display() === 'count'
  const groupSelected = () => props.groupSelected ?? display() === 'tags'
  const isTitleMode = () => props.triggerVariant === 'title'
  const isActive = () => props.selected.length > 0

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      setQuery('')
    }
  }

  const isSelected = (value: T) => props.selected.includes(value)

  const toggle = (value: T) => {
    props.onChange(
      isSelected(value) ? props.selected.filter((v) => v !== value) : [...props.selected, value]
    )
  }

  const matches = (option: MultiSelectOption<T>) => {
    const q = foldForSearch(query())
    if (!q) {
      return true
    }
    return [option.label, option.description].some((text) => foldForSearch(text).includes(q))
  }

  const visible = createMemo(() => props.options.filter(matches))
  const visibleUnselected = createMemo(() => visible().filter((o) => !isSelected(o.value)))
  const visibleSelected = createMemo(() => visible().filter((o) => isSelected(o.value)))
  const selectedOptions = createMemo(() => props.options.filter((o) => isSelected(o.value)))

  const countText = () => {
    const count = props.selected.length
    if (count === 0) {
      return props.placeholder ?? t('designSystem.multiSelect.placeholder')
    }
    if (count === 1) {
      const option = props.options.find((o) => o.value === props.selected[0])
      return option?.label ?? String(props.selected[0])
    }
    if (count === props.options.length) {
      return t('designSystem.multiSelect.allSelected')
    }
    return t('designSystem.multiSelect.countSelected', { count })
  }

  const countTriggerClass = () => {
    const base = `
      inline-flex items-center justify-between gap-2
      px-4 py-2 min-w-[160px]
      ds-field border rounded-[var(--radius-12)]
      text-sm
      focus-visible:outline-2 focus-visible:outline-[var(--color-primary-500)]
      transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)] cursor-pointer
      disabled:cursor-not-allowed disabled:opacity-50
    `
    if (!isTitleMode()) {
      return `${base} bg-[var(--color-surface)] border-[var(--color-border)] hover:border-[var(--color-neutral-300)] focus-visible:-outline-offset-2`
    }
    const titleBase = `${base} max-w-[240px]`
    if (isActive()) {
      return `${titleBase} border-[var(--color-primary-500)] bg-[var(--color-primary-50)] hover:border-[var(--color-primary-600)] focus-visible:-outline-offset-4`
    }
    return `${titleBase} border-[var(--color-border)] bg-[var(--color-surface)] hover:border-[var(--color-neutral-300)] focus-visible:-outline-offset-2`
  }

  const caretClass = () =>
    `w-4 h-4 shrink-0 transition-transform ${isOpen() ? 'rotate-180' : ''} ${
      isTitleMode() && isActive()
        ? 'text-[color:var(--color-primary-600)]'
        : 'text-[color:var(--color-text-muted)]'
    }`

  const optionItem = (option: MultiSelectOption<T>) => (
    <DropdownMenu.CheckboxItem
      checked={isSelected(option.value)}
      onChange={() => toggle(option.value)}
      closeOnSelect={false}
      title={option.description || undefined}
      class="
        flex items-center gap-3 px-3 py-2
        text-sm cursor-pointer outline-none
        rounded-[var(--radius-md)]
        transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
        hover:bg-[var(--color-neutral-100)]
        data-[highlighted]:bg-[var(--color-neutral-100)]
      "
    >
      <span
        aria-hidden="true"
        class={`
          w-4 h-4 rounded-[var(--radius-4)] border flex items-center justify-center flex-shrink-0
          transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
          ${
            isSelected(option.value)
              ? 'bg-[var(--color-primary-500)] border-[var(--color-primary-500)]'
              : 'border-[var(--color-neutral-300)] bg-[var(--color-surface)]'
          }
        `}
      >
        <Show when={isSelected(option.value)}>
          <Check class="w-3 h-3 text-[color:var(--color-on-primary)]" />
        </Show>
      </span>
      <span class="text-[color:var(--color-text)]">{option.label}</span>
    </DropdownMenu.CheckboxItem>
  )

  return (
    <DropdownMenu open={isOpen()} onOpenChange={handleOpenChange}>
      <Show
        when={!props.trigger}
        fallback={<DropdownMenu.Trigger class="outline-none">{props.trigger}</DropdownMenu.Trigger>}
      >
        <Show
          when={display() === 'tags'}
          fallback={
            <DropdownMenu.Trigger
              disabled={props.disabled}
              class={`${countTriggerClass()} ${props.class ?? ''}`}
            >
              <Show
                when={isTitleMode()}
                fallback={
                  <span class="truncate text-[color:var(--color-text)]">{countText()}</span>
                }
              >
                <span class="flex min-w-0 items-center gap-1.5">
                  <span class="truncate text-[color:var(--color-text)]" title={props.label}>
                    {props.label}
                  </span>
                  <Show when={isActive()}>
                    <Badge variant="primary" size="sm" class="shrink-0">
                      {props.selected.length}
                    </Badge>
                  </Show>
                </span>
              </Show>
              <CaretDown class={caretClass()} />
            </DropdownMenu.Trigger>
          }
        >
          <DropdownMenu.Trigger
            disabled={props.disabled}
            class={`
              flex items-center justify-between gap-2
              ds-field w-full min-h-10 px-3 py-2
              border border-[var(--color-border)] rounded-[var(--radius-12)]
              text-sm bg-[var(--color-surface)]
              hover:border-[var(--color-neutral-300)]
              focus-visible:outline-2 focus-visible:-outline-offset-2
              focus-visible:outline-[var(--color-primary-500)]
              transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)] cursor-pointer
              disabled:cursor-not-allowed disabled:opacity-50
              ${props.class ?? ''}
            `}
          >
            <div class="flex flex-wrap gap-1.5 flex-1 min-w-0">
              <Show
                when={selectedOptions().length > 0}
                fallback={
                  <span class="text-[color:var(--color-text-muted)]">
                    {props.placeholder ?? t('designSystem.multiSelect.placeholder')}
                  </span>
                }
              >
                <For each={selectedOptions()}>
                  {(option) => (
                    <span
                      class="
                        inline-flex items-center gap-1
                        px-2 py-0.5
                        bg-[var(--color-neutral-100)] text-[color:var(--color-text)]
                        text-xs font-medium rounded-[var(--radius-md)]
                        hover:bg-[var(--color-neutral-200)]
                        transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
                      "
                    >
                      {option.label}
                      <button
                        type="button"
                        aria-label={t('designSystem.multiSelect.remove', { label: option.label })}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          e.stopImmediatePropagation()
                          props.onChange(props.selected.filter((v) => v !== option.value))
                        }}
                        onPointerDown={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                        class="
                          p-0.5 -mr-0.5
                          rounded-[var(--radius-sm)]
                          hover:bg-[var(--color-neutral-300)]
                          transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
                        "
                      >
                        <X class="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </For>
              </Show>
            </div>
            <CaretDown
              class={`w-4 h-4 text-[color:var(--color-text-muted)] flex-shrink-0 transition-transform ${isOpen() ? 'rotate-180' : ''}`}
            />
          </DropdownMenu.Trigger>
        </Show>
      </Show>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          class="
            min-w-[200px] max-h-[300px]
            bg-[var(--color-surface)]
            border border-[var(--color-border)]
            rounded-[var(--radius-lg)] shadow-[var(--shadow-float)]
            z-50
            animate-[dropdown-enter_150ms_var(--ease-component)]
            flex flex-col
          "
        >
          <Show when={isTitleMode() && props.label}>
            <div class="px-3 pt-2 pb-1 text-xs font-medium text-[color:var(--color-text-muted)] leading-snug">
              {props.label}
            </div>
          </Show>

          <Show when={searchable()}>
            <div class="px-2 pt-2 pb-1 sticky top-0 bg-[var(--color-surface)] z-10">
              <div class="flex items-center gap-2 px-2.5 py-1.5 border border-[var(--color-border)] rounded-[var(--radius-12)] bg-[var(--color-background)] focus-within:outline-2 focus-within:outline-[var(--color-primary-500)]">
                <MagnifyingGlass class="w-3.5 h-3.5 text-[color:var(--color-text-muted)] flex-shrink-0" />
                <input
                  type="text"
                  value={query()}
                  onInput={(e) => setQuery(e.currentTarget.value)}
                  placeholder={t('designSystem.multiSelect.searchPlaceholder')}
                  aria-label={t('designSystem.multiSelect.searchPlaceholder')}
                  class="
                    w-full text-sm bg-transparent
                    text-[color:var(--color-text)]
                    placeholder:text-[color:var(--color-text-muted)]
                    outline-none border-none p-0
                  "
                  onKeyDown={(e) => e.stopPropagation()}
                />
                <Show when={query()}>
                  <button
                    type="button"
                    aria-label={t('designSystem.multiSelect.clear')}
                    onClick={() => setQuery('')}
                    class="p-0.5 rounded-[var(--radius-sm)] hover:bg-[var(--color-neutral-200)] transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]"
                  >
                    <X class="w-3 h-3 text-[color:var(--color-text-muted)]" />
                  </button>
                </Show>
              </div>
            </div>
          </Show>

          <Show when={showSelectAll()}>
            <div class="flex items-center gap-2 px-3 py-2 border-b border-[var(--color-border)]">
              <button
                type="button"
                onClick={() => props.onChange(props.options.map((o) => o.value))}
                class="text-xs text-[color:var(--color-primary-600)] hover:text-[color:var(--color-primary-700)] hover:underline"
              >
                {t('designSystem.multiSelect.selectAll')}
              </button>
              <span class="text-[color:var(--color-text-subtle)]">|</span>
              <button
                type="button"
                onClick={() => props.onChange([])}
                class="text-xs text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)] hover:underline"
              >
                {t('designSystem.multiSelect.clear')}
              </button>
            </div>
          </Show>

          <div class="overflow-y-auto max-h-[220px] py-1">
            <Show
              when={visible().length > 0}
              fallback={
                <div class="px-3 py-2 text-sm text-[color:var(--color-text-muted)]">
                  {query()
                    ? t('designSystem.multiSelect.noResults')
                    : t('designSystem.multiSelect.empty')}
                </div>
              }
            >
              <Show when={groupSelected()} fallback={<For each={visible()}>{optionItem}</For>}>
                <For each={visibleUnselected()}>{optionItem}</For>
                <Show when={visibleSelected().length > 0}>
                  <div class="border-t border-[var(--color-border)] mt-1 pt-1">
                    <div class="px-3 py-1.5 text-xs text-[color:var(--color-text-muted)] font-medium">
                      {t('designSystem.multiSelect.selectedGroup', {
                        count: selectedOptions().length,
                      })}
                    </div>
                    <For each={visibleSelected()}>{optionItem}</For>
                  </div>
                </Show>
              </Show>
            </Show>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu>
  )
}
